import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BellIcon, Loader2Icon, PlusIcon } from 'lucide-react';
import { buttonClasses } from '../../components/ui/Button';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { useAdminData } from '../../contexts/AdminDataContext';
import { byPlannedDate, formatDate, formatPrice } from '../../utils/format';
const ease = [0.23, 1, 0.32, 1] as const;
export function Overview() {
  const {
    cases,
    customerById,
    enquiries,
    dataLoading
  } = useAdminData();
  const newEnquiries = enquiries.filter(item => item.status === 'Ny henvendelse');
  const kpis = [{
    label: 'Nye henvendelser',
    value: newEnquiries.length,
    primary: true
  }, {
    label: 'Aktive sager',
    value: cases.filter(c => !['Færdig', 'Faktureret'].includes(c.status)).length,
    primary: false
  }, {
    label: 'Planlagte opgaver',
    value: cases.filter(c => c.status === 'Planlagt').length,
    primary: false
  }, {
    label: 'Afventer tilbud',
    value: cases.filter(c => c.status === 'Tilbud sendt').length,
    primary: false
  }, {
    label: 'Færdige – ikke faktureret',
    value: cases.filter(c => c.status === 'Færdig').length,
    primary: false
  }];
  const latest = [...cases].sort((a, b) => b.createdAt.localeCompare(a.createdAt)).slice(0, 6);
  const upcoming = cases.filter(c => c.plannedDate && !['Færdig', 'Faktureret'].includes(c.status)).sort(byPlannedDate);
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-609" data-mp="kt293rtnv4uk">
      <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-610" data-mp="kudlgg169kxa">
        <div data-id="element-611" data-mp="g1u0uz42hpiz">
          <p className="text-sm text-ink-muted" data-id="element-612" data-mp="g1asoa47yfi5">God dag</p>
          <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-613" data-mp="b4c03w07egm2">
            Overblik
          </h1>
        </div>
        <Link to="/admin/sager/ny" className={buttonClasses('primary', 'md')} data-id="element-614" data-mp="13sg66imosvpa">
          <PlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-615" data-mp="13qd6f6vqfo7d" />
          Opret sag
        </Link>
      </div>

      {dataLoading && cases.length === 0 && <p className="mt-6 flex items-center gap-2 text-[15px] text-ink-muted" data-id="element-616" data-mp="32zo8xzjiisjz">
          <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-617" data-mp="2oas56eoho1p6" />
          Henter data …
        </p>}

      {newEnquiries.length > 0 && <motion.div initial={{
      opacity: 0,
      y: -8
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.26,
      ease
    }} className="mt-6 rounded-card border border-accent/30 bg-accent-soft p-4 md:p-5" data-id="element-618" data-mp="2t7qrwc5tp6dc">
          <div className="flex flex-wrap items-center justify-between gap-3" data-id="element-619" data-mp="25cfrqgqjo5ns">
            <div className="flex items-start gap-3" data-id="element-620" data-mp="2aahat1nz04z9">
              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white text-accent-deep" data-id="element-621" data-mp="2ab94u81x5unf">
                <BellIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-622" data-mp="3igjy5cms6fu" />
              </span>
              <div data-id="element-623" data-mp="1t1er0ar25x7j">
                <ul data-id="element-624" data-mp="1t0vjlnz1ar7r">
                  {newEnquiries.slice(0, 3).map(item => <li key={item.id} className="text-[15px] font-medium text-ink" data-id="element-625" data-mp="1o2k8l9x06any">
                      Ny henvendelse fra {item.name}
                    </li>)}
                </ul>
                {newEnquiries.length > 3 && <p className="mt-1 text-sm text-ink-soft" data-id="element-626" data-mp="1xyx1w6omymi6">+ {newEnquiries.length - 3} flere</p>}
              </div>
            </div>
            <Link to="/admin/henvendelser" className={buttonClasses('primary', 'sm')} data-id="element-627" data-mp="26t1xt4immo4k">
              Se henvendelser
            </Link>
          </div>
        </motion.div>}

      <ul className="mt-7 grid gap-4 sm:grid-cols-2 xl:grid-cols-5" data-id="element-628" data-mp="2lnq24fcajgil">
        {kpis.map((kpi, index) => <motion.li key={kpi.label} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.26,
        ease,
        delay: index * 0.04
      }} className={`rounded-card border p-5 ${kpi.primary ? 'border-accent/30 bg-accent-soft' : 'border-line bg-white'}`} data-id="element-629" data-mp="rc1ja22xutsx" data-mpi={index}>
            <p className="font-display text-[2.5rem] font-bold leading-none tracking-tight text-ink" data-id="element-630" data-mp="3einvw75y1onp">
              {kpi.value}
            </p>
            <p className="mt-2 text-[15px] text-ink-soft" data-id="element-631" data-mp="3nlp0g3f5v7p9">{kpi.label}</p>
          </motion.li>)}
      </ul>

      <section className="mt-8 rounded-card border border-line bg-white" data-id="element-632" data-mp="2qvpixjh18yuj">
        <header className="flex items-center justify-between gap-3 border-b border-line px-5 py-4" data-id="element-633" data-mp="2vtr90ocvlyfi">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-634" data-mp="2zvun7kqxjh4h">Seneste sager</h2>
          <Link to="/admin/sager" className="text-sm font-medium text-accent hover:text-accent-deep" data-id="element-635" data-mp="34m2ppii6u7ia">
            Se alle
          </Link>
        </header>

        <div className="hidden md:block" data-id="element-636" data-mp="2846ed87d1cqe">
          <table className="w-full text-left text-[15px]" data-id="element-637" data-mp="2c5rzthxymzc7">
            <thead data-id="element-638" data-mp="2c470lf0pas80">
              <tr className="border-b border-line text-xs uppercase tracking-wider text-ink-muted" data-id="element-639" data-mp="24miovk9t2sl1">
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-640" data-mp="1zneg2uqsmwv8">Kunde</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-641" data-mp="1zo7hrhawaw6i">Opgave</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-642" data-mp="1vkhqul6r02ib">Dato</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-643" data-mp="1qnj0mdq7gfpe">Status</th>
                <th scope="col" className="px-5 py-3 font-medium" data-id="element-644" data-mp="1lnd4ak8zq50z">Pris</th>
                <th scope="col" className="px-5 py-3" data-id="element-645" data-mp="1lppvzp4jvtr8" />
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-646" data-mp="1gutr3oe7tbwp">
              {latest.map(item => <tr key={item.id} className="transition-colors duration-200 ease-smooth hover:bg-canvas" data-id="element-647" data-mp="1by3b112eugba">
                  <td className="px-5 py-3.5 text-ink" data-id="element-648" data-mp="17u5dgusqhdkc">
                    {customerById(item.customerId)?.name ?? item.customerName}
                  </td>
                  <td className="px-5 py-3.5 text-ink-soft" data-id="element-649" data-mp="xz3mruit83fn">
                    <span className="text-ink-faint" data-id="element-650" data-mp="oag8ok9gi1nw">#{item.number}</span> {item.title}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3.5 text-ink-soft" data-id="element-651" data-mp="t99palk1i8sa">{formatDate(item.plannedDate)}</td>
                  <td className="px-5 py-3.5" data-id="element-652" data-mp="ed23xhwwb1bf"><StatusBadge status={item.status} data-id="element-653" data-mp="edk60v68o9r9" /></td>
                  <td className="whitespace-nowrap px-5 py-3.5 font-medium text-ink" data-id="element-654" data-mp="jb3pc9hsncq2">
                    {formatPrice(item.estimate || item.fixedPrice)}
                  </td>
                  <td className="px-5 py-3.5 text-right" data-id="element-655" data-mp="173afgqwxenh1">
                    <Link to={`/admin/sager/${item.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-656" data-mp="1bypqzc7go7aa">
                      Åbn
                    </Link>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        <ul className="divide-y divide-line md:hidden" data-id="element-657" data-mp="1pza8m51n3x27">
          {latest.map(item => <li key={item.id} data-id="element-658" data-mp="1uq8sfelu7da7">
              <Link to={`/admin/sager/${item.id}`} className="block px-5 py-4" data-id="element-659" data-mp="1urk37ydp9lat">
                <div className="flex items-start justify-between gap-3" data-id="element-660" data-mp="3fg5rzx2e06sc">
                  <div className="min-w-0" data-id="element-661" data-mp="3ahugzj0cvq8j">
                    <p className="truncate text-[15px] font-medium text-ink" data-id="element-662" data-mp="3ah1jl8naed32">{item.title}</p>
                    <p className="mt-0.5 text-sm text-ink-muted" data-id="element-663" data-mp="3p5pct89nuapu">
                      #{item.number} · {customerById(item.customerId)?.name ?? item.customerName}
                    </p>
                  </div>
                  <StatusBadge status={item.status} data-id="element-664" data-mp="2wiuuoi0bfnfj" />
                </div>
                <p className="mt-2 text-sm text-ink-soft" data-id="element-665" data-mp="2rjrzrqe1uxla">
                  {formatDate(item.plannedDate)} · {formatPrice(item.estimate || item.fixedPrice)}
                </p>
              </Link>
            </li>)}
        </ul>

        {!dataLoading && latest.length === 0 && <p className="px-5 py-10 text-center text-sm text-ink-muted" data-id="element-666" data-mp="krziod3jdo8u">Der er ingen sager endnu.</p>}
      </section>

      <section className="mt-6 rounded-card border border-line bg-white" data-id="element-667" data-mp="gq5lwfwgp4dy">
        <header className="border-b border-line px-5 py-4" data-id="element-668" data-mp="2aa65u4xqb5q3">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-669" data-mp="2a7js1nk8vtzf">Kommende opgaver</h2>
        </header>
        <ul className="divide-y divide-line" data-id="element-670" data-mp="2k7u06trfab7q">
          {upcoming.map(item => <li key={item.id} className="flex flex-wrap items-center justify-between gap-3 px-5 py-4" data-id="element-671" data-mp="2k5zf9fg5ij3n">
              <div className="min-w-0" data-id="element-672" data-mp="2p0ddrqq416cj">
                <p className="truncate text-[15px] font-medium text-ink" data-id="element-673" data-mp="2sz44ng3zfrr7">{item.title}</p>
                <p className="mt-0.5 truncate text-sm text-ink-muted" data-id="element-674" data-mp="2xyfowz3k5fdj">
                  {customerById(item.customerId)?.name ?? item.customerName} · {item.address}
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-675" data-mp="37xofrj34mdvo">
                <span className="whitespace-nowrap text-sm text-ink-soft" data-id="element-676" data-mp="37v26nf0l7qbc">{formatDate(item.plannedDate)}</span>
                <StatusBadge status={item.status} data-id="element-677" data-mp="1djds3dy9pprh" />
                <Link to={`/admin/sager/${item.id}`} className={buttonClasses('secondary', 'sm')} data-id="element-678" data-mp="1dk6rftuwdczl">
                  Åbn
                </Link>
              </div>
            </li>)}
          {upcoming.length === 0 && <li className="px-5 py-8 text-center text-sm text-ink-muted" data-id="element-679" data-mp="3m13upgfnoxj0">Ingen planlagte opgaver endnu.</li>}
        </ul>
      </section>
    </div>;
}